import { Stack, Text, Title } from '@mantine/core';
import { AlertOctagon, Home, RefreshCw } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from './Button';

export interface ErrorStateProps {
  /** Short, human statement of what failed ("Could not load ranges"). */
  title: string;
  /** The underlying error, or a message string. */
  error?: unknown;
  /** Wire this to react-query's `refetch` — an error the user can't retry is a dead end. */
  onRetry?: () => void;
  /** Show a "Back to dashboard" link. Default true. */
  withHome?: boolean;
  className?: string;
}

function messageOf(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && 'message' in error) {
    const m = (error as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return undefined;
}

/**
 * ErrorState — the single failure surface for a route or panel.
 *
 * Replaces the hand-rolled `<Alert color="red">{err.message}</Alert>` that five
 * routes each reimplemented with no retry affordance. Shape mirrors
 * `EmptyState` so failure and emptiness read as the same design family.
 */
export function ErrorState({
  title,
  error,
  onRetry,
  withHome = true,
  className,
}: ErrorStateProps) {
  const navigate = useNavigate();
  const message = messageOf(error);

  return (
    <Stack
      align="center"
      justify="center"
      gap="md"
      py="xl"
      px="xl"
      {...(className !== undefined ? { className } : {})}
      style={{
        borderRadius: 12,
        border: '1px dashed rgb(var(--c-red) / 0.45)',
        backgroundColor: 'rgb(var(--c-deep))',
        textAlign: 'center',
      }}
      role="alert"
    >
      <div style={{ color: 'rgb(var(--c-red-ink))' }}>
        <AlertOctagon size={40} />
      </div>
      <Title order={4} className="font-display" style={{ color: 'rgb(var(--c-txt-bright))' }}>
        {title}
      </Title>
      <Text size="sm" c="dimmed" maw={480}>
        {message ?? 'Something went wrong. The request did not complete.'}
      </Text>
      <Stack gap="xs" style={{ flexDirection: 'row' }}>
        {onRetry ? (
          <Button variant="primary" size="sm" leftIcon={<RefreshCw size={14} />} onClick={onRetry}>
            Retry
          </Button>
        ) : null}
        {withHome ? (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Home size={14} />}
            onClick={() => void navigate({ to: '/' })}
          >
            Back to dashboard
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}
